import { useEffect, useState, type ReactNode } from 'react';
import { C } from '@ridgewill/brand';
import {
  adminApi,
  type GroupMember,
  type SecurityGroup,
  type SecurityGroupDetail,
} from '../lib/api.js';
import { useAsync, useAction } from '../lib/useAsync.js';
import { count } from '../lib/format.js';
import { roleLabel } from '../lib/roles.js';
import { InfoBubble, PrimaryButton, Screen, inputStyle } from '../components/Screen.js';
import { useCan, whyDisabled } from '../lib/capabilities.js';
import {
  AsyncBoundary,
  Badge,
  Button,
  EmptyState,
  Field,
  HoldButton,
  Note,
  OutcomeNote,
  Panel,
  Table,
  Td,
  When,
} from '../components/ui.js';
import { Modal, ModalActions } from '../components/Modal.js';
import { PickList } from '../components/PickList.js';
import { AgeBadge, isMinorBand } from '../components/AgeBand.js';
import { AppRolePicker } from '../modals/AppRoles.js';

/**
 * Security groups — who is in them, what they carry, what they reach.
 *
 * A group is a named, FLAT set of people. It owns no data, scopes no query and
 * nests inside nothing (RidgeWill/docs adr/0006). It exists because entitlement
 * used to be a property of a ROLE, so granting an app to everybody meant minting
 * a role to carry it — `keyodo-user`, whose permissions were identical to
 * `user`. A pull request and a redeploy bought a fact that was already data.
 *
 * ── MEMBERS CAN BE REMOVED HERE, BUT NOT WITH A CLICK ────────────────
 *
 * Removing somebody withdraws whatever this group gave them, and on
 * `Basic Users` that is the universal entitlement every account on the platform
 * has. The control sits at the end of a row in a list somebody is scrolling and
 * searching — the worst neighbourhood there is for a one-click irreversible
 * action, and the reason this screen shipped without one at first.
 *
 * So it is a HOLD, not a click (`HoldButton`): a second and a half, filling red
 * as it goes. A confirmation dialog is the usual answer and it is the wrong
 * one for a row action — two clicks, both reflexes, and the second is made
 * looking at a dialog rather than at the row. Holding cannot be produced by a
 * mis-click, and what you are looking at while you do it is the row itself.
 *
 * ADDING is here too, in a dialog that searches accounts — see `AddMembers`.
 * This screen shipped without it on the argument that adding needs the person
 * found first, and that the account page already IS a search for a person. That
 * was a reason to build the account page's control, not a reason to withhold
 * this one: somebody looking at a group and deciding who belongs in it would
 * otherwise leave, search, open an account and come back, once per person.
 *
 * ── WHY THE DETAIL IS A SCREEN AND NOT A MODAL ───────────────────────
 *
 * Applications, agreements and users all open a dialog. This one does not,
 * because it has a paged, searchable child collection: a table you scroll and
 * search inside a 640px dialog is a list you cannot read while doing the thing
 * you opened it for. The group is still a path segment, so a link still points
 * at one group.
 */
export function SecurityGroups({
  slug,
  navigate,
}: {
  slug?: string;
  navigate: (to: string) => void;
}) {
  return slug ? (
    <GroupDetail key={slug} slug={slug} navigate={navigate} />
  ) : (
    <GroupList navigate={navigate} />
  );
}

function GroupList({ navigate }: { navigate: (to: string) => void }) {
  const [adding, setAdding] = useState(false);
  const mayWrite = useCan('securityGroup:write');
  const { state, reload } = useAsync(() => adminApi.securityGroups());
  /**
   * Fetched for one reason: to know which applications everybody reaches anyway.
   *
   * `account` is reachable through code rather than through a grant, so a badge
   * for it says a group gets you something it does not. The server marks them
   * (`alwaysEntitled`) and this is where the answer lives, so the list has to
   * ask. A failure leaves the badge showing, which is the old behaviour rather
   * than an empty column.
   */
  const apps = useAsync(() => adminApi.applications());
  const implied =
    apps.state.status === 'ready'
      ? new Set(apps.state.data.applications.filter((a) => a.alwaysEntitled).map((a) => a.slug))
      : new Set<string>();

  return (
    <Screen
      actions={
        <PrimaryButton
          disabled={!mayWrite}
          title={mayWrite ? undefined : whyDisabled('creating security groups')}
          onClick={() => setAdding(true)}
        >
          + New group
        </PrimaryButton>
      }
    >
      <Panel title="Security groups">
        <AsyncBoundary state={state}>
          {(d) =>
            d.groups.length === 0 ? (
              <EmptyState
                title="No security groups"
                suggestion="Create one to grant an application, or a platform role, to a set of people at once."
              />
            ) : (
              <Table columns={['Group', 'Members', 'Roles', 'Applications']}>
                {d.groups.map((g) => (
                  <tr key={g.slug} className="rw-rowhover">
                    <Td>
                      <button
                        type="button"
                        onClick={() => navigate(`/security-groups/${g.slug}`)}
                        style={rowLink}
                      >
                        {g.name}
                      </button>
                      <div style={{ color: C.muted, fontSize: '.78rem' }}>
                        {g.slug}
                        {g.protected ? ' · part of the platform' : ''}
                      </div>
                    </Td>
                    <Td>{count(g.members)}</Td>
                    <Td>
                      {/*
                        BOTH KINDS OF ROLE, in one cell. A group whose only
                        authority is an app role was rendering a dash, which
                        reads as "carries nothing" — the opposite of the truth.

                        SLUGS, per the chip rule below. App roles are qualified
                        `app: slug`, because a name is unique only within its
                        application and `admin` on its own is not an answer to
                        "which role".
                      */}
                      <SlugBadges
                        values={[
                          ...g.roles,
                          ...g.appRoles.map((r) => `${r.appSlug}: ${r.name}`),
                        ]}
                        /* A group with no roles at all is the ordinary case —
                           most groups carry entitlement only — so this says
                           nothing rather than warning about it. The empty
                           APPLICATIONS set is the one worth a badge. */
                        empty={<span style={{ color: C.muted }}>—</span>}
                      />
                    </Td>
                    <Td>
                      <SlugBadges
                        values={g.applications.filter((slug) => !implied.has(slug))}
                        /* A group entitling nothing and carrying no role reaches
                           nobody and does nothing. A legitimate state — you might
                           create it before granting — and one that looks
                           identical to a working group in a table that renders an
                           empty set as a blank cell. */
                        empty={
                          g.roles.length === 0 && g.appRoles.length === 0 ? (
                            <Badge tone="warn">nothing</Badge>
                          ) : (
                            <span style={{ color: C.muted }}>—</span>
                          )
                        }
                      />
                    </Td>
                  </tr>
                ))}
              </Table>
            )
          }
        </AsyncBoundary>
      </Panel>

      {adding ? (
        <CreateGroup
          onClose={() => setAdding(false)}
          onCreated={(slug) => {
            setAdding(false);
            reload();
            // Straight into the new group: it was created to be given something,
            // and the panels that do that are one screen away.
            navigate(`/security-groups/${slug}`);
          }}
        />
      ) : null}
    </Screen>
  );
}

function GroupDetail({ slug, navigate }: { slug: string; navigate: (to: string) => void }) {
  const { state, reload } = useAsync(() => adminApi.securityGroup(slug), [slug]);

  return (
    <Screen>
      <button type="button" onClick={() => navigate('/security-groups')} style={backLink}>
        ← Security groups
      </button>

      <AsyncBoundary state={state}>
        {(group) => (
          <>
            <header style={head}>
              <div style={{ display: 'grid', gap: '.35rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap' }}>
                  <h2 style={{ margin: 0, fontSize: '1.25rem', letterSpacing: '-.01em' }}>
                    {group.name}
                  </h2>
                  <code style={{ fontSize: '.8rem', color: C.muted }}>{group.slug}</code>
                  {group.protected ? <Badge>system group</Badge> : null}
                </div>
                {group.description ? (
                  <p style={{ margin: 0, fontSize: '.88rem', color: C.muted, maxWidth: '62ch' }}>
                    {group.description}
                  </p>
                ) : null}
              </div>
              <div style={{ marginLeft: 'auto' }}>
                <DeleteGroup group={group} onDeleted={() => navigate('/security-groups')} />
              </div>
            </header>

            {/*
              MEMBERS LAST. The configuration panels are short and are what
              somebody came to change; the membership is long, paged, and on
              `basic-users` is every account on the platform. Putting it first
              pushed the three editable panels below a scroll on the group most
              likely to be opened.
            */}
{/*
              THE THREE "what this group carries" PANELS ABREAST, with
              entitlements in the middle. Each holds a short list — a group
              carries a handful of roles and reaches a handful of applications —
              and stacked they left three wide bands of empty table between the
              reader and the membership.

              Entitlements sits between the two role panels because it is the
              one people come here for, and because the two role lists read as a
              pair.

              `auto-fit` with a floor rather than three fixed columns, so a
              narrow window folds them instead of crushing a name against its
              Remove button.
            */}
            <div style={panelRow}>
              <Roles group={group} onChanged={reload} />
              <Entitlements group={group} onChanged={reload} />
              <GroupAppRoles group={group} onChanged={reload} />
            </div>

            <Members
              slug={group.slug}
              groupName={group.name}
              total={group.members}
              onChanged={reload}
            />
          </>
        )}
      </AsyncBoundary>
    </Screen>
  );
}

const PAGE = 50;

/**
 * Who is in this group.
 *
 * SEARCHED ON THE SERVER, not filtered in the browser. `Basic Users` holds every
 * account on the platform, so a client-side filter over the current page would
 * report "no match" for somebody who is plainly a member — the defect the chip
 * search was built to fix on the Users screen, and this is the list most likely
 * to reproduce it.
 */
function Members({
  slug,
  groupName,
  total,
  onChanged,
}: {
  slug: string;
  groupName: string;
  total: number;
  /** The group's own record carries the member COUNT, so a removal here makes
   *  the header stale until the parent refetches too. */
  onChanged: () => void;
}) {
  const [term, setTerm] = useState('');
  const [applied, setApplied] = useState('');
  const [offset, setOffset] = useState(0);
  const [adding, setAdding] = useState(false);

  /**
   * A quarter of a second between the last keystroke and the request.
   *
   * Instant search on a table this size means a query per character, against a
   * set of `ILIKE '%…%'` comparisons no index can serve. The delay is short
   * enough to read as instant and long enough that typing a name is one request
   * rather than nine.
   */
  useEffect(() => {
    const id = setTimeout(() => {
      setApplied(term.trim());
      setOffset(0);
    }, 250);
    return () => clearTimeout(id);
  }, [term]);

  const { state, reload } = useAsync(
    () => adminApi.securityGroupMembers(slug, { limit: PAGE, offset, term: applied || undefined }),
    [slug, applied, offset],
  );
  const action = useAction();

  const remove = (member: GroupMember) =>
    void action
      .run(async () => {
        const r = await adminApi.removeSecurityGroupMember(slug, member.id);
        const who = member.email ?? member.name ?? member.id;
        // A no-op is reported as one. Saying "removed" for a request that
        // removed nothing is how somebody comes to believe an account is out of
        // a group it is still in.
        return r.removed
          ? `${who} is no longer in ${groupName}.`
          : `${who} was not in ${groupName}.`;
      })
      .then(() => {
        reload(true);
        onChanged();
      });

  return (
    <Panel
      title="Members"
      info={
        <InfoBubble label="About membership">
          Adding somebody gives them every application, platform role and app role this group
          carries; removing takes all of it back. Anything they hold directly, or through another
          group, is untouched.
        </InfoBubble>
      }
      action={
        <span style={headerActions}>
          <span style={{ fontSize: '.8rem', color: C.muted }}>
            {count(total)} {total === 1 ? 'account' : 'accounts'}
          </span>
          <Button disabled={action.running} onClick={() => setAdding(true)}>
            + Add members
          </Button>
        </span>
      }
    >

      <OutcomeNote outcome={action.outcome} />

      <div style={{ padding: '.5rem 1.2rem .8rem' }}>
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          style={inputStyle}
          placeholder="Search this group by name, email or id…"
          aria-label="Search members of this group"
        />
      </div>

      <AsyncBoundary state={state}>
        {(d) =>
          d.members.length === 0 ? (
            <EmptyState
              title={applied ? 'No members match' : 'Nobody is in this group'}
              suggestion={
                applied
                  ? 'Try a different term. The search covers name, email address and account id.'
                  : 'Add members here, or from an account’s own page.'
              }
            />
          ) : (
            <>
              <Table columns={['Account', 'Status', 'Joined', '']} actionsLast>
                {d.members.map((m) => (
                  <tr key={m.id} className="rw-rowhover">
                    <Td>
                      <div style={{ fontWeight: 600 }}>{m.email ?? <Redacted />}</div>
                      <div style={{ color: C.muted, fontSize: '.78rem' }}>
                        {m.name ?? '—'}
                      </div>
                    </Td>
                    <Td>
                      <MemberStatus member={m} />
                    </Td>
                    <Td>
                      <When iso={m.addedAt} />
                    </Td>
                    <Td action>
                      <HoldButton
                        disabled={action.running}
                        onHold={() => remove(m)}
                        title={`Hold for a second and a half to take this account out of ${groupName}.`}
                      >
                        Remove
                      </HoldButton>
                    </Td>
                  </tr>
                ))}
              </Table>
              <div style={pager}>
                <span style={{ fontSize: '.82rem', color: C.muted }}>
                  {count(d.total === 0 ? 0 : offset + 1)}&ndash;{count(offset + d.members.length)} of{' '}
                  {count(d.total)}
                </span>
                <span style={{ display: 'flex', gap: '.4rem', marginLeft: 'auto' }}>
                  <Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
                    Previous
                  </Button>
                  <Button
                    disabled={offset + d.members.length >= d.total}
                    onClick={() => setOffset(offset + PAGE)}
                  >
                    Next
                  </Button>
                </span>
              </div>
            </>
          )
        }
      </AsyncBoundary>

      {adding ? (
        <AddMembers
          slug={slug}
          groupName={groupName}
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            reload(true);
            onChanged();
          }}
        />
      ) : null}
    </Panel>
  );
}

/**
 * Find people, and put them in this group.
 *
 * -- ADDING LIVES HERE AS WELL AS ON THE ACCOUNT PAGE -----------------
 *
 * This screen shipped without it, on the argument that adding needs the person
 * found first and the account page already is a search for a person. That was a
 * reason to build the account page's control, not a reason to withhold this one:
 * somebody looking at a group and deciding who belongs in it would otherwise
 * have to leave, search, open an account and come back, once per person.
 *
 * A MODAL rather than an inline row, because it contains a search of a different
 * collection. Inline, the panel would hold two result lists at once -- the
 * group's members and the accounts that are not in it -- with two search boxes a
 * few pixels apart and nothing to say which one you were typing into.
 *
 * -- ALREADY-MEMBERS ARE REPORTED, NOT HIDDEN ------------------------
 *
 * The search covers every account rather than "accounts not in this group":
 * scoping it would mean the person somebody searched for vanishing from the
 * results with no explanation, which reads as "no such account". So they can be
 * picked, the server answers `alreadyMembers`, and the outcome says which were
 * which.
 */
function AddMembers({
  slug,
  groupName,
  onClose,
  onAdded,
}: {
  slug: string;
  groupName: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [term, setTerm] = useState('');
  const [applied, setApplied] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  /** Who was picked, by name and age band, kept across searches for the warning below. */
  const [pickedPeople, setPickedPeople] = useState<Map<string, { label: string; minor: boolean }>>(new Map());
  const action = useAction();
  /**
   * TESTERS ONLY, and a warning rather than a block (Jon, 2026-10-14,
   * org-admin#187). A member under 18 in Testers is asked to accept the Testing
   * Terms, which a guardian must accept too; staff may have a reason, and seeing
   * it is what stops the mistake.
   */
  const minorsPicked =
    slug === 'testers' ? picked.filter((id) => pickedPeople.get(id)?.minor).map((id) => pickedPeople.get(id)!.label) : [];

  useEffect(() => {
    const id = setTimeout(() => setApplied(term.trim()), 250);
    return () => clearTimeout(id);
  }, [term]);

  const results = useAsync(
    () => adminApi.users({ terms: applied ? [applied] : [], limit: 25 }),
    [applied],
  );

  return (
    <Modal
      title={`Add members to ${groupName}`}
      onClose={onClose}
      width={620}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={action.running || picked.length === 0}
            onClick={() =>
              void action
                .run(async () => {
                  const r = await adminApi.addSecurityGroupMembers(slug, picked);
                  const added = r.added.length
                    ? `Added ${count(r.added.length)} ${r.added.length === 1 ? 'account' : 'accounts'}.`
                    : 'Nothing to add.';
                  const already = r.alreadyMembers.length
                    ? ` ${count(r.alreadyMembers.length)} ${
                        r.alreadyMembers.length === 1 ? 'was' : 'were'
                      } already in this group.`
                    : '';
                  return added + already;
                })
                .then(onAdded)
            }
          >
            {action.running ? 'Adding...' : 'Add'}
          </Button>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />
      {minorsPicked.length > 0 ? (
        <Note tone="warn">
          {minorsPicked.length === 1 ? `${minorsPicked[0]} is` : `${minorsPicked.join(', ')} are`} under 18. Testers
          are asked to accept the Testing Terms, which a guardian must accept too. Check{' '}
          {minorsPicked.length === 1 ? 'they' : 'these accounts'} should be testers before adding them.
        </Note>
      ) : null}
      <input
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        style={inputStyle}
        placeholder="Search every account by name, email or id..."
        aria-label="Search accounts to add"
      />

      <AsyncBoundary state={results.state}>
        {(d) =>
          d.users.length === 0 ? (
            <p style={{ fontSize: '.88rem', color: C.muted, margin: '.8rem 0 0' }}>
              {applied ? 'No account matches.' : 'No accounts.'}
            </p>
          ) : (
            <div style={{ display: 'grid', gap: '.4rem', marginTop: '.8rem' }}>
              {d.users.map((u) => (
                <label
                  key={u.id}
                  style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.88rem' }}
                >
                  <input
                    type="checkbox"
                    checked={picked.includes(u.id)}
                    onChange={() => {
                      setPicked(
                        picked.includes(u.id)
                          ? picked.filter((v) => v !== u.id)
                          : [...picked, u.id],
                      );
                      setPickedPeople(
                        new Map(pickedPeople).set(u.id, {
                          label: u.name || u.email || 'A closed account',
                          minor: isMinorBand(u.ageBand),
                        }),
                      );
                    }}
                  />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {u.email ?? <em style={{ color: C.muted }}>account closed</em>}
                    {u.name ? <span style={{ color: C.muted }}> &middot; {u.name}</span> : null}
                  </span>
                  {/* The age band on every group (org-admin#187), so nobody is
                      added as an adult by mistake. */}
                  <AgeBadge band={u.ageBand} />
                </label>
              ))}
              {d.total > d.users.length ? (
                <p style={{ fontSize: '.8rem', color: C.muted, margin: '.3rem 0 0' }}>
                  Showing {count(d.users.length)} of {count(d.total)}. Narrow the search to see the
                  rest.
                </p>
              ) : null}
            </div>
          )
        }
      </AsyncBoundary>
    </Modal>
  );
}

function MemberStatus({ member }: { member: GroupMember }) {
  if (member.pseudonymized) return <Badge tone="neutral">closed</Badge>;
  if (member.banned) return <Badge tone="danger">disabled</Badge>;
  return <Badge tone="good">active</Badge>;
}

/** A closed account's name and address are cleared, and saying so beats an
 *  empty cell that reads as a rendering fault. */
function Redacted() {
  return <span style={{ color: C.muted, fontStyle: 'italic' }}>account closed</span>;
}

/**
 * Which applications this group entitles.
 *
 * The same rows `PUT /admin/applications/:id/grants` edits from the other end.
 * Both screens write the COMPLETE set, so the last save wins visibly rather than
 * two admins each half-succeeding — which is the trade the auth service makes
 * deliberately, and the reason this composes a whole set from what is on screen
 * rather than sending "add this one".
 */
function Entitlements({ group, onChanged }: { group: SecurityGroup; onChanged: () => void }) {
  const [adding, setAdding] = useState(false);
  const all = useAsync(() => adminApi.applications());
  const action = useAction();

  const save = (next: string[], describeIt: (r: string[]) => string) =>
    void action
      .run(async () => describeIt((await adminApi.setSecurityGroupApplications(group.slug, next)).applications))
      .then(onChanged);

  return (
    <Panel
      title="Applications entitled"
      action={
        <Button disabled={action.running} onClick={() => setAdding(true)}>
          + Add
        </Button>
      }
    >
      <OutcomeNote outcome={action.outcome} />

      <AsyncBoundary state={all.state}>
        {(d) => {
          const bySlug = new Map(d.applications.map((a) => [a.slug, a]));
          /**
           * Applications everybody reaches anyway are not listed.
           *
           * A grant row for one is real and does nothing: `account` is reachable
           * through code, not through a grant, so a row for it offered a Remove
           * that changes nothing and invited somebody to wonder why. The server
           * says which those are (`alwaysEntitled`) rather than the portal
           * matching on the string 'account' — a copy of a rule that lives in
           * the auth service is a copy that stops agreeing with it.
           */
          const shown = group.applications.filter((slug) => !bySlug.get(slug)?.alwaysEntitled);

          return shown.length === 0 ? (
            <EmptyState
              title="Entitles nothing"
              suggestion="Members reach no application through this group. Add one, or grant it from the application's own screen."
            />
          ) : (
            <Table columns={['Application', '']} actionsLast>
              {shown.map((slug) => (
                <tr key={slug} className="rw-rowhover">
                  <Td>
                    {/* The NAME, with the slug beneath it. The slug is what an
                        API call and a token audience carry, so it stays visible
                        — but it is not what a person calls the thing. */}
                    <div>{bySlug.get(slug)?.name ?? slug}</div>
                    <code style={{ fontSize: '.78rem', color: C.muted }}>{slug}</code>
                  </Td>
                  <Td action>
                    <HoldButton
                      disabled={action.running}
                      title={`Hold for a second and a half to stop ${group.name} reaching ${slug}.`}
                      onHold={() =>
                        save(
                          group.applications.filter((a) => a !== slug),
                          () => `${slug} is no longer reachable through ${group.name}.`,
                        )
                      }
                    >
                      Remove
                    </HoldButton>
                  </Td>
                </tr>
              ))}
            </Table>
          );
        }}
      </AsyncBoundary>

      {adding ? (
        <AsyncBoundary state={all.state}>
          {(d) => (
            <PickList
              title={`Entitle an application to ${group.name}`}
              hint="Members reach it immediately. Everyone in this group, including anybody added later."
              options={d.applications
                .filter(
                  (a) => a.active && !a.alwaysEntitled && !group.applications.includes(a.slug),
                )
                .map((a) => ({ value: a.slug, label: `${a.name} (${a.slug})` }))}
              emptyMessage="Every active application is already entitled to this group."
              onClose={() => setAdding(false)}
              onConfirm={(picked) => {
                setAdding(false);
                save([...group.applications, ...picked], (r) =>
                  r.length ? `Entitled to ${r.join(', ')}.` : 'All entitlements removed.',
                );
              }}
            />
          )}
        </AsyncBoundary>
      ) : null}
    </Panel>
  );
}

/**
 * Which PLATFORM roles this group carries.
 *
 * ── `global-admin` is not in the list, and not because it is filtered here ──
 *
 * The server sends `groupAssignable` for exactly this control. A role that is
 * the way back in must not be reachable by editing a membership list, so it is
 * refused on write — and offering a checkbox the server will refuse is how a
 * screen teaches somebody that the portal is broken. The list comes from the
 * server so the two cannot drift.
 */
function Roles({ group, onChanged }: { group: SecurityGroup; onChanged: () => void }) {
  const [adding, setAdding] = useState(false);
  const known = useAsync(() => adminApi.roles());
  const action = useAction();
  const labels = known.state.status === 'ready' ? known.state.data.labels : undefined;

  const save = (next: string[], describeIt: (r: string[]) => string) =>
    void action
      .run(async () => describeIt((await adminApi.setSecurityGroupRoles(group.slug, next)).roles))
      .then(onChanged);

  return (
    <Panel
      title="Platform roles held"
      info={
        <InfoBubble label="About platform roles">
          A platform role added here reaches every account in this group, including anybody added
          later. What each role permits is decided in the auth service and reviewed in a pull
          request.
        </InfoBubble>
      }
      action={
        <Button disabled={action.running} onClick={() => setAdding(true)}>
          + Add
        </Button>
      }
    >
      <OutcomeNote outcome={action.outcome} />

      {group.roles.length === 0 ? (
        <EmptyState
          title="Holds no platform role"
          suggestion="This group grants entitlement only."
        />
      ) : (
        <>
          {/*
            NO WARNING BANNER HERE. There was one, saying that N accounts hold
            these roles through this group and taking one away takes it from all
            of them. That is what a group IS — the panel is titled "Roles held"
            on a page whose other panel is the membership — so the banner spent a
            block of amber restating the screen.

            A warning that says something obvious is worse than none: it is the
            one people learn to scroll past, and the next warning on this screen
            inherits that.
          */}
          <Table columns={['Role', '']} actionsLast>
            {group.roles.map((role) => (
              <tr key={role} className="rw-rowhover">
                <Td>
                  <div>{roleLabel(role, labels)}</div>
                  <code style={{ fontSize: '.78rem', color: C.muted }}>{role}</code>
                </Td>
                <Td action>
                  <HoldButton
                    disabled={action.running}
                    title={`Hold for a second and a half to withdraw ${role} from ${group.name}.`}
                    onHold={() =>
                      save(
                        group.roles.filter((r) => r !== role),
                        () => `${role} withdrawn from ${group.name}.`,
                      )
                    }
                  >
                    Remove
                  </HoldButton>
                </Td>
              </tr>
            ))}
          </Table>
        </>
      )}

      {adding ? (
        <AsyncBoundary state={known.state}>
          {(d) => (
            <PickList
              title={`Give ${group.name} a platform role`}
              hint="Every member gains it at once, and so does anybody added to this group afterwards."
              options={(d.groupAssignable ?? d.roles)
                .filter((r) => !group.roles.includes(r))
                .map((r) => ({
                  value: r,
                  label: d.labels?.[r] ? `${d.labels[r].label} — ${d.labels[r].description}` : r,
                }))}
              emptyMessage="This group already carries every role a group may carry."
              onClose={() => setAdding(false)}
              onConfirm={(picked) => {
                setAdding(false);
                save([...group.roles, ...picked], (r) =>
                  r.length ? `Carries ${r.join(', ')}.` : 'All roles withdrawn.',
                );
              }}
            />
          )}
        </AsyncBoundary>
      ) : null}
    </Panel>
  );
}

/**
 * App roles this group carries.
 *
 * ── THE COMPLETE SET COMES FROM THE SERVER, AND HAS TO ───────────────
 *
 * The write endpoint takes the group's whole set of app roles ACROSS EVERY
 * APPLICATION. A control that could only see one application's roles would
 * compose that set from a partial view and silently revoke every role the group
 * carries elsewhere — a whole-set write driven by a fraction of the state.
 *
 * So the group's own read returns them all, and every write here is composed
 * from that list rather than from what a picker happens to be showing.
 */
function GroupAppRoles({
  group,
  onChanged,
}: {
  group: SecurityGroupDetail;
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const apps = useAsync(() => adminApi.applications());
  const action = useAction();
  const held = group.appRoles.map((r) => r.id);

  const save = (next: string[], describeIt: () => string) =>
    void action
      .run(async () => {
        await adminApi.setSecurityGroupAppRoles(group.slug, next);
        return describeIt();
      })
      .then(onChanged);

  return (
    <Panel
      title="App roles held"
      info={
        <InfoBubble label="About app roles">
          Roles belonging to one application. Everybody in this group holds them the next time that
          application mints them a token, and what they mean is the application&rsquo;s business
          rather than this platform&rsquo;s &mdash; auth stores the name and cannot check it.
        </InfoBubble>
      }
      action={
        <Button disabled={action.running} onClick={() => setAdding(true)}>
          + Add
        </Button>
      }
    >

      <OutcomeNote outcome={action.outcome} />

      {group.appRoles.length === 0 ? (
        <EmptyState
          title="Holds no app roles"
          suggestion="Add one to give everybody in this group a name the application will recognise."
        />
      ) : (
        <Table columns={['Role', 'Application', '']} actionsLast>
          {group.appRoles.map((role) => (
            <tr key={role.id} className="rw-rowhover">
              <Td>
                <div>{role.label}</div>
                {/* The string the application matches on — the only part that
                    actually travels in a token. */}
                <code style={{ fontSize: '.78rem', color: C.muted }}>{role.name}</code>
              </Td>
              <Td>{role.appSlug}</Td>
              <Td action>
                <HoldButton
                  disabled={action.running}
                  title={`Hold for a second and a half to stop ${group.name} holding ${role.name}.`}
                  onHold={() =>
                    save(
                      held.filter((id) => id !== role.id),
                      () => `${role.name} no longer held by ${group.name}.`,
                    )
                  }
                >
                  Remove
                </HoldButton>
              </Td>
            </tr>
          ))}
        </Table>
      )}

      {adding ? (
        <AsyncBoundary state={apps.state}>
          {(d) => (
            <AppRolePicker
              applications={d.applications
                .filter((a) => a.active)
                .map((a) => ({ id: a.id, slug: a.slug, name: a.name }))}
              alreadyHeld={held}
              onClose={() => setAdding(false)}
              onConfirm={(picked: string[]) => {
                setAdding(false);
                save([...held, ...picked], () =>
                  `Carrying ${picked.length} more app role${picked.length === 1 ? '' : 's'}.`,
                );
              }}
            />
          )}
        </AsyncBoundary>
      ) : null}
    </Panel>
  );
}

/**
 * Deleting a group.
 *
 * ── Disabled for the two the platform names in code, not hidden ──────
 *
 * `basic-users` and `admins` are protected by SLUG in the auth service, so the
 * button could be absent. It is disabled and labelled instead: an admin looking
 * for how to delete `Basic Users` and finding nothing concludes the portal is
 * missing a feature and goes looking in the database. A control that says why it
 * cannot be used answers the question where it was asked.
 *
 * The confirmation names how many accounts it touches, because that is the part
 * that is not obvious — the cascade withdraws every role and entitlement this
 * group conferred, from everybody in it.
 */
function DeleteGroup({ group, onDeleted }: { group: SecurityGroup; onDeleted: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const action = useAction();

  if (group.protected) {
    return (
      <Button
        disabled
        // A no-op rather than making `onClick` optional on Button: a handler
        // that may be left off is one that gets left off by accident, on a
        // button that then looks fine and does nothing.
        onClick={() => {}}
        title="This group is part of the platform and cannot be deleted."
      >
        Delete group
      </Button>
    );
  }

  return (
    <>
      <Button tone="danger" onClick={() => setConfirming(true)}>
        Delete group
      </Button>
      {confirming ? (
        <Modal
          title={`Delete ${group.name}?`}
          onClose={() => setConfirming(false)}
          footer={
            <ModalActions>
              <Button onClick={() => setConfirming(false)}>Cancel</Button>
              <HoldButton
                disabled={action.running}
                title={`Hold for a second and a half to delete ${group.name}.`}
                onHold={() =>
                  void action
                    .run(async () => {
                      const r = await adminApi.deleteSecurityGroup(group.slug);
                      return `Deleted. ${count(r.affectedUsers)} ${
                        r.affectedUsers === 1 ? 'account' : 'accounts'
                      } updated.`;
                    })
                    .then(onDeleted)
                }
              >
                {action.running ? 'Deleting...' : 'Delete group'}
              </HoldButton>
            </ModalActions>
          }
        >
          <OutcomeNote outcome={action.outcome} />
          {/*
            ONE LINE. This listed what the group conferred and to how many
            accounts, then explained that the accounts themselves survive.

            All of it was true and none of it was being read: a confirmation is
            a place to decide, not to learn the data model, and a dialog with two
            paragraphs in it teaches people to click past dialogs. The page
            behind this one already shows the roles, the entitlements and the
            membership, which is where somebody weighing it up should be looking.

            The pause that used to come from reading now comes from the hold.
          */}
          <p style={{ fontSize: '.9rem', color: C.textMid, margin: 0 }}>
            This cannot be undone.
          </p>
        </Modal>
      ) : null}
    </>
  );
}

function CreateGroup({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (slug: string) => void;
}) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const action = useAction();

  return (
    <Modal
      title="Create a security group"
      onClose={onClose}
      footer={
        <ModalActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            disabled={action.running || !slug.trim() || !name.trim()}
            onClick={() =>
              void action
                .run(async () => {
                  await adminApi.createSecurityGroup({
                    slug: slug.trim(),
                    name: name.trim(),
                    description: description.trim() || undefined,
                  });
                  return 'Created.';
                })
                .then(() => onCreated(slug.trim()))
            }
          >
            {action.running ? 'Creating…' : 'Create'}
          </Button>
        </ModalActions>
      }
    >
      <OutcomeNote outcome={action.outcome} />
      <Field
        label="Slug"
        hint="PERMANENT. Groups the platform protects are named by slug in code, so a rename would be a way to un-protect one. Lowercase letters, digits and hyphens."
      >
        <input
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          style={inputStyle}
          placeholder="support-staff"
        />
      </Field>
      <Field label="Name">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={inputStyle}
          placeholder="Support staff"
        />
      </Field>
      <Field
        label="Description"
        hint="Shown on the group and in this list. Worth writing: it is the only place somebody learns why taking a person out of this group is a meaningful act rather than tidying."
      >
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          style={inputStyle}
        />
      </Field>
      <p style={{ fontSize: '.86rem', color: C.muted, margin: 0 }}>
        A new group carries nothing and contains nobody. Give it an application or a role next.
      </p>
    </Modal>
  );
}

/** Tick some of what is not already held, and confirm. One control for both
 *  "+ Add" buttons, because they differ only in what they are a list of. */

function SlugBadges({ values, empty }: { values: string[]; empty: ReactNode }) {
  if (values.length === 0) return <>{empty}</>;
  return (
    <span style={{ display: 'inline-flex', gap: '.3rem', flexWrap: 'wrap' }}>
      {values.map((v) => (
        <Badge key={v}>{v}</Badge>
      ))}
    </span>
  );
}

const rowLink = {
  background: 'none',
  border: 'none',
  padding: 0,
  font: 'inherit',
  fontWeight: 600,
  color: C.brand,
  cursor: 'pointer',
  textAlign: 'left' as const,
};

const backLink = {
  ...rowLink,
  fontWeight: 500,
  fontSize: '.85rem',
  color: C.muted,
  marginBottom: '.2rem',
};

const head = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '1rem',
  flexWrap: 'wrap' as const,
  padding: '0 .1rem .2rem',
};

/**
 * THE CHIP RULE (Jon, 2026-08-28).
 *
 * A chip is an identifier, so it carries the SLUG. A row label — the leftmost
 * column of a table — is what a person calls the thing, so it carries the NAME,
 * with the slug beneath it in monospace.
 *
 * The rule exists because the two were mixed: this column rendered
 * `support-admin` beside `keyodo: App Admin`, one slug and one display name in
 * the same cell, and whichever a reader learns to expect the other looks like a
 * different kind of thing.
 */

/** A panel header that carries an info bubble next to its action. */
const headerActions = {
  display: 'flex',
  alignItems: 'center',
  gap: '.5rem',
};

/** Panels abreast on a wide window, folding on a narrow one. `auto-fit` with a
 *  floor rather than fixed columns, so they collapse instead of crushing a name
 *  against its Remove button. */
const panelRow = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))',
  gap: '.9rem',
  alignItems: 'start' as const,
};

const pager = {
  display: 'flex',
  alignItems: 'center',
  gap: '.6rem',
  padding: '.75rem 1.2rem',
};
